import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Play, Pause, Square, FastForward } from 'lucide-react';

export default function ReplayTimeline({ 
  duration = 0, // Total duration in seconds
  currentTime = 0, // Current time in seconds
  onTimeChange, // Callback when time changes (scrubbing)
  onPlay, // Callback when play is clicked
  onPause, // Callback when pause is clicked
  onStop, // Callback when stop is clicked
  onFastForward, // Callback when fast forward is clicked
  isPlaying = false,
  isFastForwarding = false,
  title = 'Replay',
  startTimestamp = null,
  endTimestamp = null
}) {
  const { isDark } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const sliderContainerRef = useRef(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    // Handle both string and Date objects
    let date;
    if (typeof timestamp === 'string') {
      // Ensure we're parsing as UTC if it doesn't have timezone info
      date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return '';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return '';
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  // Handle slider click/drag
  const handleSliderInteraction = useCallback((e) => {
    if (!sliderContainerRef.current || duration === 0) return;
    
    const rect = sliderContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    
    if (onTimeChange) {
      onTimeChange(newTime);
    }
  }, [duration, onTimeChange]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSliderInteraction(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSliderInteraction(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 ${
        isDark
          ? 'bg-gray-900/95 border-t border-gray-700'
          : 'bg-white/95 border-t border-gray-200'
      }`}
    >
      <div className="max-w-4xl mx-auto">
        {/* Controls */}
        <div className="flex items-center gap-4 mb-3">
          {/* Play/Pause button */}
          <button
            onClick={isPlaying ? onPause : onPlay}
            className={`p-2 rounded-md transition-colors ${
              isDark
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>

          {/* Stop button */}
          <button
            onClick={onStop}
            className={`p-2 rounded-md transition-colors ${
              isDark
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <Square size={18} />
          </button>

          {/* Fast Forward button */}
          <button
            onClick={onFastForward}
            className={`p-2 rounded-md transition-colors ${
              isFastForwarding
                ? isDark
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-500 text-white'
                : isDark
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <FastForward size={20} />
          </button>

          {/* Time display */}
          <div className="flex-1">
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <span>{formatTime(currentTime)}</span>
              <span className="mx-2">/</span>
              <span>{formatTime(duration)}</span>
            </div>
            {startTimestamp && endTimestamp && (
              <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {formatTimestamp(startTimestamp)} → {formatTimestamp(endTimestamp)}
              </div>
            )}
          </div>
        </div>

        {/* Scrubber */}
        <div
          ref={sliderContainerRef}
          className="relative h-2 cursor-pointer"
          onMouseDown={handleMouseDown}
        >
          {/* Background track */}
          <div
            className={`absolute inset-0 rounded-full ${
              isDark ? 'bg-gray-700' : 'bg-gray-200'
            }`}
          />
          
          {/* Progress track */}
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all ${
              isDark ? 'bg-purple-500' : 'bg-purple-600'
            }`}
            style={{ width: `${progress}%` }}
          />

          {/* Thumb */}
          <div
            ref={sliderRef}
            className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full transition-all ${
              isDark ? 'bg-purple-400' : 'bg-purple-500'
            }`}
            style={{ left: `calc(${progress}% - 8px)` }}
          />
        </div>
      </div>
    </div>
  );
}

